import { useEffect, useRef, useState } from 'react';

const SCRAMBLE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

/**
 * Renders `text`, but on first scroll-into-view it "decodes" into place:
 * every character shows random noise, then locks in left-to-right until
 * the real word is fully revealed. Runs once per mount.
 *
 * Usage: <ScrambleText text="MINUTES" />
 */
export default function ScrambleText({ text, className = '', duration = 900 }) {
  const [display, setDisplay] = useState(text);
  const spanRef = useRef(null);
  const playedRef = useRef(false);

  useEffect(() => {
    const node = spanRef.current;
    if (!node) return undefined;

    let frame = null;
    let start = null;

    function tick(now) {
      if (start === null) start = now;
      const elapsed = now - start;
      const progress = Math.min(1, elapsed / duration);
      const revealCount = Math.floor(progress * text.length);

      let next = '';
      for (let i = 0; i < text.length; i += 1) {
        const char = text[i];
        if (char === ' ') {
          next += ' ';
        } else if (i < revealCount) {
          next += char;
        } else {
          next += SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)];
        }
      }
      setDisplay(next);

      if (progress < 1) {
        frame = requestAnimationFrame(tick);
      } else {
        setDisplay(text);
      }
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !playedRef.current) {
            playedRef.current = true;
            frame = requestAnimationFrame(tick);
          }
        });
      },
      { threshold: 0.4 }
    );
    observer.observe(node);

    return () => {
      observer.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [text, duration]);

  return (
    <span ref={spanRef} className={className}>
      {display}
    </span>
  );
}